// Events
import {
  HEADER_MOBILE_CLICK,
  MODAL_CLOSE,
  MODAL_CLOSED,
  MODAL_OPEN,
  SEARCH_QUERY,
  SEARCH_QUERY_SUGGESTION,
  SEARCH_RESET,
  SEARCH_SUBMIT,
} from 'events';
// Lib
import ScrollYObserver from 'lib/ScrollYObserver';
import debounce from 'lodash/debounce';
import { component } from 'picoapp';
import { disablePageScroll, enablePageScroll } from 'scroll-lock';

import choozy from 'utils/choozy';
// Utils
import { repaint } from 'utils/node';

const getLeftTranslation = (el, desiredWidth) => {
  const { width, left } = el.getBoundingClientRect();

  return left - (window.innerWidth - desiredWidth) / 2 - (desiredWidth - width);
};

export default component(async (node, ctx) => {
  const {
    navigationToggle,
    navigation,
    searchInputContainer,
    headerSearchInput,
    closeSearch,
    header,
  } = choozy(node);

  // CSS Variable names
  const SEARCH_INPUT_X = '--search-input-x-offset';
  const SEARCH_INPUT_WIDTH = '--search-input-width';

  const scrollYObserver = new ScrollYObserver();

  scrollYObserver.observe(
    ({ reachedTop }) => {
      node.classList[reachedTop ? 'add' : 'remove']('show');
      const productNavigation = document.querySelector('[data-product-navigation]');
      if (productNavigation) {
        const headerOffset = header.offsetHeight;
        const closestParent = productNavigation.closest('.sticky');

        if (reachedTop) {
          closestParent.style.top = `${headerOffset}px`;
        } else {
          closestParent.style.top = '0';
        }
      }
    },
    {
      upThreshold: 75,
      downThreshold: 50,
      minY: 1,
    },
  );

  scrollYObserver.start();

  let searchActive = false;

  const openSearchModal = () => {
    if (searchActive) return;

    searchActive = true;

    ctx.emit(MODAL_OPEN, null, { id: 'search-popup' });

    searchInputContainer.style.setProperty(
      SEARCH_INPUT_WIDTH,
      `${searchInputContainer.offsetWidth}px`,
    );
    repaint(searchInputContainer);

    node.classList.add('open');

    searchInputContainer.style.setProperty(
      SEARCH_INPUT_X,
      `-${getLeftTranslation(searchInputContainer.parentElement, 430)}px`,
    );
  };

  const handleEnterKey = (event) => {
    const currentValue = headerSearchInput.value.trim();

    if (event.key === 'Enter' && currentValue !== '') ctx.emit(SEARCH_SUBMIT, null, 'header');
  };

  headerSearchInput?.addEventListener('focus', openSearchModal);
  headerSearchInput?.addEventListener('keyup', handleEnterKey);

  window.addEventListener(
    'resize',
    debounce(() => {
      if (searchActive === false) return;

      searchInputContainer?.style.setProperty(
        SEARCH_INPUT_X,
        `-${getLeftTranslation(searchInputContainer.parentElement, 430)}px`,
      );
    }, 200),
  );

  const openNavigation = () => {
    document.documentElement.style.setProperty(
      '--header-offset',
      `${header.getBoundingClientRect().y - 1}px`,
    );
    navigation.setAttribute('open', '');
    navigationToggle.setAttribute('open', '');
    header.setAttribute('open', '');
    disablePageScroll(navigation);
    ctx.emit(MODAL_CLOSE, null, { id: 'search-popup' });
  };

  const closeNavigation = () => {
    navigation.removeAttribute('open');
    navigationToggle.removeAttribute('open');
    header.removeAttribute('open');
    enablePageScroll(navigation);
  };

  const toggleNavigation = () => {
    const isOpen = navigation.hasAttribute('open');

    if (isOpen) {
      closeNavigation();
    } else {
      openNavigation();
    }
  };

  const loadNavigationStatement = () => {
    const params = new URLSearchParams(window.location.search.slice(1));

    if (params.get('open-header-menu') === 'true') {
      if (!window.theme.isMobile) toggleNavigation();
      params.delete('open-header-menu');
      window.history.replaceState(
        {},
        '',
        [...params].length > 0 ? `?${params.toString()}` : window.location.pathname,
      );
    }
  };

  navigationToggle?.addEventListener('click', toggleNavigation);

  node.addEventListener('click', () => {
    if (!window.theme.isMobile) ctx.emit(HEADER_MOBILE_CLICK);
  });

  loadNavigationStatement();

  headerSearchInput?.addEventListener(
    'input',
    debounce(() => {
      const { search = {} } = ctx.getState();
      ctx.emit(SEARCH_QUERY, { search: { ...search, query: headerSearchInput.value } }, 'header');
    }, 400),
  );

  ctx.on(SEARCH_QUERY, ({ search }, emitterId) => {
    if (emitterId === 'header') return;

    if (search.query !== headerSearchInput.value) {
      headerSearchInput.value = search.query;
    }
  });

  ctx.on(SEARCH_QUERY_SUGGESTION, (state, query) => {
    headerSearchInput.value = query;
  });

  closeSearch?.addEventListener('click', () => {
    ctx.emit(MODAL_CLOSE, null, { id: 'search-popup' });
  });

  ctx.on(MODAL_CLOSE, (state, { id }) => {
    if (id !== 'search-popup') return;

    headerSearchInput.value = '';
    node.classList.remove('open');

    if (!searchActive) return;

    searchActive = false;

    searchInputContainer.style.setProperty(
      SEARCH_INPUT_WIDTH,
      `${searchInputContainer.parentElement.offsetWidth}px`,
    );
    node.classList.remove('open');
  });

  ctx.on(MODAL_CLOSED, (state, { id }) => {
    if (id !== 'search-popup') return;

    ctx.emit(SEARCH_RESET, null);
  });

  ctx.on(MODAL_OPEN, (state, { id }) => {
    if (id !== 'search-popup') return;

    node.classList.add('open');
    closeNavigation();
  });
});
